import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Popover,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

/**
 * Equine dental chart using modified Triadan numbering system.
 * Upper jaw: 100s (right) and 200s (left)
 * Lower jaw: 300s (right) and 400s (left)
 *
 * Status colors:
 *   normal  = #4CAF50 (green)
 *   mild    = #FFC107 (amber)
 *   moderate = #FF9800 (orange)
 *   severe  = #F44336 (red)
 *   missing = #9E9E9E (grey)
 *   extracted = #795548 (brown)
 */

type ToothStatus = 'normal' | 'mild' | 'moderate' | 'severe' | 'missing' | 'extracted';

interface ToothFinding {
  status: ToothStatus;
  findings: string;
}

interface DentalChartProps {
  value?: Record<string, ToothFinding>;
  onChange?: (value: Record<string, ToothFinding>) => void;
  readOnly?: boolean;
}

const STATUS_COLORS: Record<ToothStatus, string> = {
  normal: '#4CAF50',
  mild: '#FFC107',
  moderate: '#FF9800',
  severe: '#F44336',
  missing: '#9E9E9E',
  extracted: '#795548',
};

const STATUS_LABELS: Record<ToothStatus, string> = {
  normal: 'Normal',
  mild: 'Mild abnormality',
  moderate: 'Moderate abnormality',
  severe: 'Severe abnormality',
  missing: 'Missing/unerupted',
  extracted: 'Extracted',
};

// Quadrant tooth numbers using modified Triadan
// Equine teeth: incisors (01-03), canine (04), wolf (05), premolars (06-08), molars (09-11)
const QUADRANTS = [
  { label: 'Upper Right (100s)', prefix: 1, teeth: [11, 10, 9, 8, 7, 6, 5, 4, 3, 2, 1], side: 'right', jaw: 'upper' },
  { label: 'Upper Left (200s)', prefix: 2, teeth: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11], side: 'left', jaw: 'upper' },
  { label: 'Lower Right (300s)', prefix: 3, teeth: [11, 10, 9, 8, 7, 6, 5, 4, 3, 2, 1], side: 'right', jaw: 'lower' },
  { label: 'Lower Left (400s)', prefix: 4, teeth: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11], side: 'left', jaw: 'lower' },
];

const toothId = (prefix: number, tooth: number) => `${prefix}${String(tooth).padStart(2, '0')}`;
const toothLabel = (prefix: number, tooth: number) => `${prefix}${String(tooth).padStart(2, '0')}`;

interface ToothProps {
  id: string;
  label: string;
  finding?: ToothFinding;
  onClick: (id: string, anchor: HTMLElement) => void;
  readOnly?: boolean;
}

const Tooth: React.FC<ToothProps> = ({ id, label, finding, onClick, readOnly }) => {
  const status = finding?.status || 'normal';
  const color = STATUS_COLORS[status];
  const hasFindings = finding && finding.findings?.trim();

  return (
    <Tooltip
      title={hasFindings ? `${STATUS_LABELS[status]}: ${finding!.findings}` : STATUS_LABELS[status]}
      placement="top"
    >
      <Box
        component="button"
        onClick={e => !readOnly && onClick(id, e.currentTarget as HTMLElement)}
        sx={{
          width: 32,
          height: 40,
          background: color,
          border: hasFindings ? '2px solid #333' : '1px solid rgba(0,0,0,0.2)',
          borderRadius: 1,
          cursor: readOnly ? 'default' : 'pointer',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.25,
          transition: 'opacity 0.15s, transform 0.1s',
          mx: 0.25,
          '&:hover': readOnly ? {} : { opacity: 0.8, transform: 'scale(1.1)' },
          outline: 'none',
          p: 0,
        }}
      >
        <Typography sx={{ fontSize: '7px', color: '#fff', fontWeight: 700, lineHeight: 1 }}>
          {label}
        </Typography>
        {hasFindings && (
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'rgba(0,0,0,0.4)' }} />
        )}
      </Box>
    </Tooltip>
  );
};

const DentalChart: React.FC<DentalChartProps> = ({ value = {}, onChange, readOnly = false }) => {
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);
  const [selectedTooth, setSelectedTooth] = useState<string | null>(null);
  const [editStatus, setEditStatus] = useState<ToothStatus>('normal');
  const [editFindings, setEditFindings] = useState('');

  const handleToothClick = useCallback((id: string, anchor: HTMLElement) => {
    setSelectedTooth(id);
    setEditStatus(value[id]?.status || 'normal');
    setEditFindings(value[id]?.findings || '');
    setPopoverAnchor(anchor);
  }, [value]);

  const handleSave = () => {
    if (!selectedTooth || !onChange) return;
    const updated = { ...value };
    if (editStatus === 'normal' && !editFindings.trim()) {
      delete updated[selectedTooth];
    } else {
      updated[selectedTooth] = { status: editStatus, findings: editFindings };
    }
    onChange(updated);
    setPopoverAnchor(null);
    setSelectedTooth(null);
  };

  const handleClear = () => {
    if (!selectedTooth || !onChange) return;
    const updated = { ...value };
    delete updated[selectedTooth];
    onChange(updated);
    setPopoverAnchor(null);
    setSelectedTooth(null);
  };

  const upperRight = QUADRANTS[0];
  const upperLeft = QUADRANTS[1];
  const lowerRight = QUADRANTS[2];
  const lowerLeft = QUADRANTS[3];

  const renderRow = (q: typeof QUADRANTS[0]) => (
    <Box display="flex" alignItems="center" sx={{ my: 0.25 }}>
      <Typography variant="caption" sx={{ width: 90, fontSize: '9px', color: 'text.secondary', flexShrink: 0 }}>
        {q.label}
      </Typography>
      {q.teeth.map(t => {
        const id = toothId(q.prefix, t);
        return (
          <Tooth
            key={id}
            id={id}
            label={toothLabel(q.prefix, t)}
            finding={value[id]}
            onClick={handleToothClick}
            readOnly={readOnly}
          />
        );
      })}
    </Box>
  );

  return (
    <Box>
      <Box sx={{ overflowX: 'auto', pb: 1 }}>
        <Box sx={{ minWidth: 500 }}>
          {/* Upper jaw */}
          <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: 'text.secondary', mb: 0.5 }}>
            ── UPPER JAW ──
          </Typography>
          {renderRow(upperRight)}
          {renderRow(upperLeft)}

          {/* Midline */}
          <Box sx={{ borderBottom: '2px dashed', borderColor: 'divider', my: 1 }} />

          {/* Lower jaw */}
          <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: 'text.secondary', mb: 0.5 }}>
            ── LOWER JAW ──
          </Typography>
          {renderRow(lowerRight)}
          {renderRow(lowerLeft)}
        </Box>
      </Box>

      {/* Legend */}
      <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
        {Object.entries(STATUS_COLORS).map(([status, color]) => (
          <Box key={status} display="flex" alignItems="center" gap={0.5}>
            <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: color, border: '1px solid rgba(0,0,0,0.2)' }} />
            <Typography variant="caption" color="text.secondary">{STATUS_LABELS[status as ToothStatus]}</Typography>
          </Box>
        ))}
      </Box>

      {/* Findings count */}
      {Object.keys(value).filter(k => value[k]?.status !== 'normal').length > 0 && (
        <Box mt={1}>
          <Typography variant="caption" color="text.secondary">
            {Object.keys(value).filter(k => value[k]?.status !== 'normal').length} tooth/teeth with findings:&nbsp;
          </Typography>
          {Object.entries(value)
            .filter(([, f]) => f.status !== 'normal')
            .map(([id, f]) => (
              <Chip
                key={id}
                label={`${id} (${STATUS_LABELS[f.status]})`}
                size="small"
                sx={{ mr: 0.5, mb: 0.5, bgcolor: STATUS_COLORS[f.status], color: '#fff' }}
                onDelete={readOnly ? undefined : () => {
                  const updated = { ...value };
                  delete updated[id];
                  onChange?.(updated);
                }}
              />
            ))}
        </Box>
      )}

      {/* Tooth editing popover */}
      <Popover
        open={!!popoverAnchor}
        anchorEl={popoverAnchor}
        onClose={() => setPopoverAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Box sx={{ p: 2, width: 280 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle2">Tooth {selectedTooth}</Typography>
            <IconButton size="small" onClick={() => setPopoverAnchor(null)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={editStatus}
              label="Status"
              onChange={e => setEditStatus(e.target.value as ToothStatus)}
            >
              {Object.entries(STATUS_LABELS).map(([s, label]) => (
                <MenuItem key={s} value={s}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: STATUS_COLORS[s as ToothStatus] }} />
                    {label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Findings / Notes"
            fullWidth
            size="small"
            multiline
            rows={2}
            value={editFindings}
            onChange={e => setEditFindings(e.target.value)}
            sx={{ mb: 1.5 }}
          />
          <Box display="flex" gap={1} justifyContent="flex-end">
            <Button size="small" color="error" onClick={handleClear}>Clear</Button>
            <Button size="small" variant="contained" onClick={handleSave}>Save</Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

export default DentalChart;
